import type { RegisterOptions } from 'react-hook-form';

import clsx from 'clsx';
import React from 'react';

import { useFormatText } from '@/hooks/useFormatText';

interface IInput {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
  registerOptions?: RegisterOptions;
  error?: string | string[];
  name: string;
  label?: string;
  placeholder?: string;
  type?: string;
  rightIcon?: {
    icon: string | React.ReactNode;
    onClick?: () => void;
    color?: string;
  };
  leftIcon?: {
    icon: string | React.ReactNode;
    onClick?: () => void;
    color?: string;
  };
  min?: number | string;
  max?: number | string;
  value?: string | number;
  rightIconBg?: string;
  leftIconBg?: string;
  className?: string;
  inputClassName?: string;
  inputWrapperClassName?: string;
  readOnly?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  required?: boolean;
  dataTestId?: string;
}

interface IInputLabel {
  label?: string;
  name: string;
  required?: boolean;
  t: (key: string) => string;
}

interface IInputIcon {
  iconData?: {
    icon: string | React.ReactNode;
    onClick?: () => void;
    color?: string;
  };
  iconBg?: string;
  disabled?: boolean;
  isLeft?: boolean;
}

interface IInputError {
  error?: string | string[];
  name: string;
}

const InputLabel = ({ label, name, required, t }: IInputLabel) => {
  if (!label) return null;
  return (
    <label
      htmlFor={name}
      className="block text-sm font-medium text-neutral-primary leading-5"
    >
      {label}{' '}
      {required ? (
        <span className="text-red-500">*</span>
      ) : (
        <span className="text-neutral-800 text-sm">
          ({t('common.optional')})
        </span>
      )}
    </label>
  );
};

const InputIcon = ({ iconData, iconBg, disabled, isLeft }: IInputIcon) => {
  if (!iconData?.icon) return null;
  return (
    <button
      onClick={iconData.onClick}
      type="button"
      className={clsx(
        'flex items-center justify-center outline-none font-normal',
        isLeft ? 'ps-3 rounded-e-lg' : 'px-3 rounded-e-lg min-w-10.25'
      )}
      style={{ color: iconData.color, background: iconBg }}
      disabled={disabled}
      tabIndex={iconData.onClick ? 0 : -1}
    >
      {iconData.icon}
    </button>
  );
};

const InputError = ({ error, name }: IInputError) => {
  if (!error) return null;
  const errors = Array.isArray(error) ? error : [error];
  return (
    <>
      {errors.map((e, i) => (
        <p
          key={i}
          id={`${name}-error`}
          className="text-sm font-normal text-error-default"
          role="alert"
        >
          {e}
        </p>
      ))}
    </>
  );
};

const Input = ({
  disabled,
  error,
  type = 'text',
  register,
  registerOptions,
  name,
  placeholder,
  rightIcon,
  leftIcon,
  min,
  max,
  value,
  label,
  rightIconBg,
  leftIconBg,
  className,
  inputClassName,
  inputWrapperClassName,
  readOnly,
  autoFocus = false,
  required = false,
  onBlur,
  onFocus,
  dataTestId = 'input',
}: IInput) => {
  const registered = register?.(name, {
    valueAsNumber: type === 'number',
    ...(registerOptions || {}),
  });

  const { formatText } = useFormatText();

  const stateClasses = clsx({
    'border border-neutral-light-200 bg-neutral-100 focus-within:outline-none':
      disabled,
    '!border-error-default focus-within:outline-none bg-white':
      !disabled && error,
    '!border-neutral-light-200 focus-within:!outline-none': readOnly,
  });

  return (
    <div className={clsx('w-full flex flex-col gap-1.5', className)}>
      <InputLabel
        label={label}
        name={name}
        required={required}
        t={formatText}
      />
      <div
        className={clsx(
          'group w-full rounded-lg flex shadow-s gap-2 bg-white border border-neutral-light-200 focus-within:outline-2 focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] text-neutral-900 font-semibold text-base',
          inputWrapperClassName,
          stateClasses
        )}
      >
        <InputIcon
          iconData={leftIcon}
          iconBg={leftIconBg}
          disabled={disabled}
          isLeft
        />
        <input
          id={name}
          disabled={disabled}
          readOnly={readOnly}
          type={type}
          placeholder={placeholder}
          {...(register ? {} : { value })}
          step={type === 'number' ? 0.01 : undefined}
          {...registered}
          onBlur={(e) => {
            registered?.onBlur?.(e);
            onBlur?.(e);
          }}
          onFocus={onFocus}
          onChange={(e) => {
            registered?.onChange?.(e);
          }}
          autoFocus={autoFocus}
          className={clsx(
            'w-full focus:outline-none border-none py-2.5 px-3 rounded-lg text-sm font-medium text-neutral-900',
            'placeholder:font-medium placeholder:text-neutral-disabled autofill:bg-transparent!',
            disabled &&
              'text-neutral-disabled cursor-not-allowed bg-neutral-50 focus:outline-none',
            leftIcon?.icon && 'ps-0!',
            inputClassName
          )}
          min={min}
          max={max}
          data-testid={dataTestId}
          aria-label={label ? undefined : placeholder || name}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${name}-error` : undefined}
          aria-required={required}
        />
        <InputIcon
          iconData={rightIcon}
          iconBg={rightIconBg}
          disabled={disabled}
        />
      </div>
      <InputError error={error} name={name} />
    </div>
  );
};

export default Input;
